import axios from './axiosConfig'
import { downloadBlob } from './blobDownload'
import type { ApiResourceFilter } from './consumerService'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080'

// =============================================================================
// Types & Interfaces
// =============================================================================

export interface NotificationTypeCount {
  notificationTypeId: number
  notificationTypeName: string
  count: number
}

/**
 * How many of an alert's messages on one channel are in one delivery status:
 * pending, sent (accepted by the provider), received (delivered), read, failed,
 * cancelled or skipped.
 */
export interface NotificationChannelStatus {
  channel: string
  status: string
  count: number
  /** The worker's last delivery error on this channel, when the Gateway sends one. */
  lastError?: string | null
}

export type AlertState = 'open' | 'closed'

/** An alert is open until it has a closing time or the Gateway marks it not open. */
const toAlertState = (
  alert: Pick<NotificationAlert, 'isOpen' | 'closedAt'>
): AlertState => (alert.isOpen && !alert.closedAt ? 'open' : 'closed')

export interface NotificationAlert {
  alertId: number
  alertTypeId: number
  alertTypeName: string
  openedAt: string
  closedAt: string | null
  createdAt: string
  updatedAt: string
  metadata: string
  isOpen: boolean
  /** Derived from isOpen and closedAt; the table shows this, not the two. */
  alertState: AlertState
  presetId: number
  replaceReasonId: number | null
  note: string | null
  notificationCount: number
  lastSentAt: string
  /** The furthest any of the alert's messages got; null when it has none. */
  status: string | null
  channels: NotificationChannelStatus[]
  /** Live CRM tasks linked to the alert: in progress wins over open; null when none. */
  crmStatus: CrmChip | null
}

export type CrmChip = 'in_progress' | 'open'

const toCrmChip = (value: unknown): CrmChip | null =>
  value === 'in_progress' || value === 'open' ? value : null

export interface PaginatedNotificationResponse {
  total: number
  pageSize: number
  page: number
  result: NotificationAlert[]
  // These readers resolve with an empty page when the request fails, so a
  // caller cannot otherwise tell "no alerts" from "the Gateway answered an
  // error". Legacy callers ignore the flag and keep the old behaviour.
  failed?: boolean
}

export interface NotificationMessage {
  notificationId: number
  alertId: number
  channel: string
  recipient: string
  body: string
  sender: string
  sentAt: string | null
  createdAt: string
  status: string
  // Gateway adds this to the DTO in T5-B item 4; absent until then, so
  // consumers (NotificationStatusChip) must treat it as optional.
  lastError?: string | null
}

export interface NotificationAlertEnriched extends NotificationAlert {
  // Consumer fields
  meterId?: number
  consumerName?: string
  waterCard?: number
  address?: string
  severity?: string
  alertType?: string
  fromDate?: string
  toDate?: string | null
}

export interface AlertType {
  alertTypeId: number
  alertTypeName: string
}

export interface AllConsumerNotification {
  meterNumber: number
  consumerName?: string
  address?: string
  waterCard?: string | number
  alertId: number
  alertTypeId: number
  alertTypeName: string
  severity: number
  openedAt: string
  closedAt: string | null
  createdAt: string
  updatedAt: string
  metadata: string
  isOpen: boolean
  premiseId: number
  replaceReasonId: number | null
  note: string | null
  notificationCount: number
  lastSentAt: string
  status: string
  targetTypes: string[]
}

export interface PaginatedAllNotificationResponse {
  total: number
  pageSize: number
  page: number
  result: AllConsumerNotification[]
  /** See PaginatedNotificationResponse.failed. */
  failed?: boolean
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Fetch open notification counts grouped by type.
 */
export const getOpenNotifications = async (
  accountId: string | number,
  page: number = 1,
  size: number = 500
): Promise<NotificationTypeCount[]> => {
  try {
    const res = await axios.get<{
      success: boolean
      data: {
        rValue: number
        payerId: number
        sensorStatus: boolean
        total: number
        pageSize: number
        page: number
        result: NotificationTypeCount[]
      }
    }>(`${API_BASE_URL}/api/notifications/${accountId}/open`, {
      params: { page, size },
      headers: { 'Content-Type': 'application/json' },
    })

    return res.data?.data?.result ?? []
  } catch (error) {
    console.error('[notificationService] Failed to fetch notifications:', error)
    return []
  }
}

/**
 * The Gateway takes one window form: whole days (`startDate`/`endDate`,
 * YYYY-MM-DD) or instants (`from`/`to`, sent as ISO with Z).
 */
export type AlertListWindow = {
  startDate?: string
  endDate?: string
  from?: Date
  to?: Date
}

const windowParams = (w?: AlertListWindow) => {
  const params: Record<string, string> = {}
  if (w?.startDate) params.startDate = w.startDate
  if (w?.endDate) params.endDate = w.endDate
  if (w?.from) params.from = w.from.toISOString()
  if (w?.to) params.to = w.to.toISOString()
  return params
}

/**
 * Fetch detailed notifications for a specific consumer
 */
export const getConsumerNotifications = async (
  accountId: string | number,
  premiseId: string | number,
  options?: AlertListWindow & {
    page?: number
    size?: number
    sortBy?: string
    sortOrder?: string
    includeClosed?: boolean
  }
): Promise<PaginatedNotificationResponse> => {
  const empty: PaginatedNotificationResponse = {
    total: 0,
    pageSize: 0,
    page: 1,
    result: [],
  }
  try {
    const params: Record<string, string | number | boolean> =
      windowParams(options)
    if (options?.page) params.page = options.page
    if (options?.size) params.size = options.size
    if (options?.sortBy) params.sortBy = options.sortBy
    if (options?.sortOrder) params.sortOrder = options.sortOrder
    if (options?.includeClosed !== undefined)
      params.includeClosed = options.includeClosed

    const res = await axios.get<{
      success: boolean
      data: PaginatedNotificationResponse
    }>(`${API_BASE_URL}/api/notifications/${accountId}/consumer/${premiseId}`, {
      params,
      headers: { 'Content-Type': 'application/json' },
    })

    const page = res.data?.data
    if (!page) return empty
    return {
      ...page,
      result: (page.result ?? []).map(alert => ({
        ...alert,
        alertState: toAlertState(alert),
        channels: alert.channels ?? [],
        crmStatus: toCrmChip(alert.crmStatus),
      })),
    }
  } catch (error) {
    console.error(
      '[notificationService] Failed to fetch consumer notifications:',
      error
    )
    return { ...empty, failed: true }
  }
}

/**
 * Fetch messages for a specific alert
 */
export const getAlertMessages = async (
  accountId: string | number,
  alertId: number,
  page: number = 1,
  size: number = 50
): Promise<NotificationMessage[]> => {
  try {
    const res = await axios.get<{
      success: boolean
      data: {
        total: number
        pageSize: number
        page: number
        result: NotificationMessage[]
      }
    }>(
      `${API_BASE_URL}/api/notifications/${accountId}/alert/${alertId}/messages`,
      {
        params: { page, size },
        headers: { 'Content-Type': 'application/json' },
      }
    )

    return res.data?.data?.result ?? []
  } catch (error) {
    console.error(
      '[notificationService] Failed to fetch alert messages:',
      error
    )
    return []
  }
}

export type SentMessageKind = 'alert' | 'resend' | 'manual'

export interface SentMessageDelivery {
  notificationId: number
  channel: string
  address: string
  status: string
  sentAt: string | null
  receivedAt: string | null
  lastError: string | null
}

/** One card: one send to one recipient, with all its channels and addresses. */
export interface SentMessage {
  messageId: number
  kind: SentMessageKind
  alertId: number | null
  alertTypeName: string | null
  recipientName: string | null
  channels: string[]
  addresses: string[]
  /** Plain text; an HTML body is reduced to its text here. */
  bodyText: string
  /** True when the stored body still holds {{variables}}. */
  bodyIsTemplate: boolean
  createdAt: string
  sentAt: string | null
  receivedAt: string | null
  status: string
  lastError: string | null
  deliveries: SentMessageDelivery[]
}

export interface SentMessagePage {
  total: number
  page: number
  pageSize: number
  result: SentMessage[]
}

type SentMessageWire = Omit<SentMessage, 'bodyText' | 'kind'> & {
  kind: 'ALERT' | 'RESEND' | 'MANUAL'
  body: string
  bodyFormat: 'TEXT' | 'HTML'
}

const htmlToText = (html: string): string =>
  new DOMParser()
    .parseFromString(html.replace(/<br\s*\/?>|<\/p>/gi, '\n'), 'text/html')
    .body.textContent?.trim() ?? ''

const toSentMessage = ({
  body,
  bodyFormat,
  kind,
  ...rest
}: SentMessageWire): SentMessage => ({
  ...rest,
  kind: kind.toLowerCase() as SentMessageKind,
  bodyText: bodyFormat === 'HTML' ? htmlToText(body) : body,
})

type SentMessagePaging = { page: number; size: number }

const getSentMessages = async (
  url: string,
  paging: SentMessagePaging,
  window?: { from: Date; to: Date }
): Promise<SentMessagePage> => {
  const res = await axios.get<{
    data: Omit<SentMessagePage, 'result'> & { result: SentMessageWire[] }
  }>(`${API_BASE_URL}${url}`, {
    params: { ...paging, ...windowParams(window) },
    headers: { 'Content-Type': 'application/json' },
  })
  const page = res.data.data
  return { ...page, result: page.result.map(toSentMessage) }
}

/**
 * Every message the consumer got, newest first: the "כללי" tab. Rejects on
 * failure, so the panel can tell "no messages" from "the Gateway failed".
 */
export const getConsumerSentMessages = (
  accountId: string | number,
  premiseId: string | number,
  paging: SentMessagePaging,
  window?: { from: Date; to: Date }
): Promise<SentMessagePage> =>
  getSentMessages(
    `/api/notifications/${accountId}/consumer/${premiseId}/sent-messages`,
    paging,
    window
  )

/** The messages of one alert: the "התראה ספציפית" tab. Rejects on failure. */
export const getAlertSentMessages = (
  accountId: string | number,
  alertId: number,
  paging: SentMessagePaging
): Promise<SentMessagePage> =>
  getSentMessages(
    `/api/notifications/${accountId}/alert/${alertId}/sent-messages`,
    paging
  )

/**
 * Fetch alert types for an account (site).
 *
 * Rejects on failure, so a filter can tell an empty list ("the site defines no
 * alert types") apart from an unanswered request.
 */
export const getAlertTypes = async (
  accountId: string | number
): Promise<AlertType[]> => {
  const res = await axios.get<{
    success: boolean
    data: AlertType[]
  }>(`${API_BASE_URL}/api/notifications/${accountId}/alert-types`, {
    headers: { 'Content-Type': 'application/json' },
  })

  return res.data?.data ?? []
}

/**
 * Fetch all notifications across all consumers for an account (site)
 */
export const getAllConsumerNotifications = async (
  accountId: string | number,
  options?: {
    startDate?: string
    endDate?: string
    page?: number
    size?: number
    sortBy?: string
    sortOrder?: string
    includeClosed?: boolean
    alertTypeId?: number
    severity?: number
    meterNumber?: number
    consumerName?: string
    waterCard?: string
    alertName?: string
    openedFrom?: string
    openedTo?: string
    closedFrom?: string
    closedTo?: string
  }
): Promise<PaginatedAllNotificationResponse> => {
  try {
    const params: Record<string, string | number | boolean> = {}
    if (options?.startDate) params.startDate = options.startDate
    if (options?.endDate) params.endDate = options.endDate
    if (options?.page) params.page = options.page
    if (options?.size) params.size = options.size
    if (options?.sortBy) params.sortBy = options.sortBy
    if (options?.sortOrder) params.sortOrder = options.sortOrder
    if (options?.includeClosed !== undefined)
      params.includeClosed = options.includeClosed
    if (options?.alertTypeId !== undefined)
      params.alertTypeId = options.alertTypeId
    if (options?.severity !== undefined) params.severity = options.severity
    if (options?.meterNumber !== undefined)
      params.meterNumber = options.meterNumber
    if (options?.consumerName) params.consumerName = options.consumerName
    if (options?.waterCard) params.waterCard = options.waterCard
    if (options?.alertName) params.alertName = options.alertName
    if (options?.openedFrom) params.openedFrom = options.openedFrom
    if (options?.openedTo) params.openedTo = options.openedTo
    if (options?.closedFrom) params.closedFrom = options.closedFrom
    if (options?.closedTo) params.closedTo = options.closedTo

    const res = await axios.get<{
      success: boolean
      data: PaginatedAllNotificationResponse
    }>(`${API_BASE_URL}/api/notifications/${accountId}/all`, {
      params,
      headers: { 'Content-Type': 'application/json' },
    })

    const payload = res.data?.data
    return {
      total: payload?.total ?? payload?.result?.length ?? 0,
      pageSize: payload?.pageSize ?? options?.size ?? 10,
      page: payload?.page ?? options?.page ?? 1,
      result: payload?.result ?? [],
    }
  } catch (error) {
    console.error(
      '[notificationService] Failed to fetch all consumer notifications:',
      error
    )
    return {
      total: 0,
      pageSize: options?.size ?? 10,
      page: options?.page ?? 1,
      result: [],
      failed: true,
    }
  }
}

/**
 * Map alert type name from Hebrew to English severity level
 */
export const mapAlertTypeToSeverity = (alertTypeName: string): string => {
  // This mapping should match your alert type configurations
  const severityMap: Record<string, string> = {
    'צריכה יומית חריגה': 'נמוכה',
    'צריכה יומית חריגה מאוד': 'בינונית',
    'צריכה רצופה 24 שעות': 'גבוהה',
    'נזילה מהירה': 'גבוהה',
    'הפרש מדידה חריג': 'בינונית',
    'הפרש מדידה חריג מאוד': 'גבוהה',
    'חבלה פיזית': 'גבוהה',
    מגנט: 'גבוהה',
    'סוללה נמוכה': 'נמוכה',
    'סוללה נמוכה מאוד': 'בינונית',
  }

  return severityMap[alertTypeName] || 'נמוכה'
}

/**
 * Fetch all notifications with enriched consumer data
 * This fetches consumers and notifications separately, then joins them
 */
export const getAllNotificationsWithConsumerData = async (
  accountId: string | number,
  startDate?: string,
  endDate?: string
): Promise<NotificationAlertEnriched[]> => {
  try {
    // Step 1: Fetch all consumers
    const consumersResponse = await axios.post<{
      success: boolean
      data: {
        total: number
        pageSize: number
        page: number
        result: Array<{
          premiseId: number
          siteId: number
          meterNumber: number
          meterId: number
          billingId: number
          locationId: string
          consumerName?: string
          address?: string
          // Add other consumer fields as needed
        }>
      }
    }>(
      `${API_BASE_URL}/api/consumer/${accountId}?page=1&size=5000&sortBy=premiseId&sortOrder=ASC`,
      { apiResources: [] },
      { headers: { 'Content-Type': 'application/json' } }
    )

    const consumers = consumersResponse.data?.data?.result || []

    // Create a map for quick lookup: premiseId -> consumer
    const consumerMap = new Map(consumers.map(c => [c.premiseId, c]))

    // Step 2: Fetch notifications for all consumers
    // TODO: Replace this with the actual endpoint structure
    // Option A: If there's an endpoint to get all notifications at once
    // Option B: Fetch notifications for each consumer (slower but works)

    const allNotifications: NotificationAlert[] = []

    // Using Option B for now - fetch per consumer
    // You can optimize this if backend provides a bulk endpoint
    for (const consumer of consumers) {
      try {
        const response = await getConsumerNotifications(
          accountId,
          consumer.premiseId, // or consumer.meterId - check which one backend uses
          { startDate, endDate }
        )
        allNotifications.push(...response.result)
      } catch (error) {
        console.error(
          `Failed to fetch notifications for consumer ${consumer.premiseId}:`,
          error
        )
      }
    }

    // Step 3: Enrich notifications with consumer data
    const enrichedNotifications: NotificationAlertEnriched[] =
      allNotifications.map(notification => {
        // Find the matching consumer
        // Note: You need to determine which field in notification corresponds to premiseId
        // This might be in metadata or a separate field
        const consumerId = notification.presetId // Replace with actual field
        const consumer = consumerMap.get(consumerId)

        return {
          ...notification,
          meterId: consumer?.meterNumber,
          consumerName: consumer?.consumerName || 'לא ידוע',
          waterCard: consumer?.billingId,
          address: consumer?.address,
          severity: mapAlertTypeToSeverity(notification.alertTypeId.toString()),
          alertType: getAlertTypeName(notification.alertTypeId),
          fromDate: notification.openedAt,
          toDate: notification.closedAt,
        }
      })

    return enrichedNotifications
  } catch (error) {
    console.error(
      '[notificationService] Failed to fetch notifications with consumer data:',
      error
    )
    return []
  }
}

/**
 * Get alert type name from ID
 */
const getAlertTypeName = (alertTypeId: number): string => {
  const alertTypeMap: Record<number, string> = {
    1: 'צריכה רצופה 24 שעות',
    2: 'צריכה יומית חריגה',
    3: 'צריכה יומית חריגה מאוד',
    4: 'נזילה מהירה',
    5: 'הפרש מדידה חריג',
    6: 'הפרש מדידה חריג מאוד',
    7: 'חבלה פיזית',
    8: 'מגנט',
    9: 'סוללה נמוכה',
    10: 'סוללה נמוכה מאוד',
    11: 'נזילה',
    // Add more mappings as needed
  }

  return alertTypeMap[alertTypeId] || `התראה ${alertTypeId}`
}

// =============================================================================
// Server-side Excel Export
// =============================================================================

/**
 * Export all notifications to Excel.
 * GET /api/notifications/{accountId}/all/export
 */
export const exportAllNotificationsExcel = async (
  accountId: string | number,
  params: {
    startDate?: string
    endDate?: string
    sortBy?: string
    sortOrder?: string
    includeClosed?: boolean
    alertTypeId?: number
    langKey?: string
    severity?: number
    meterNumber?: number
    consumerName?: string
    waterCard?: string
    alertName?: string
    openedFrom?: string
    openedTo?: string
    closedFrom?: string
    closedTo?: string
  },
  onProgress?: (percent: number) => void
): Promise<void> => {
  const q = new URLSearchParams()
  if (params.startDate) q.append('startDate', params.startDate)
  if (params.endDate) q.append('endDate', params.endDate)
  if (params.sortBy) q.append('sortBy', params.sortBy)
  if (params.sortOrder) q.append('sortOrder', params.sortOrder)
  if (params.includeClosed !== undefined)
    q.append('includeClosed', String(params.includeClosed))
  if (params.alertTypeId !== undefined)
    q.append('alertTypeId', String(params.alertTypeId))
  if (params.severity !== undefined)
    q.append('severity', String(params.severity))
  if (params.meterNumber !== undefined)
    q.append('meterNumber', String(params.meterNumber))
  if (params.consumerName) q.append('consumerName', params.consumerName)
  if (params.waterCard) q.append('waterCard', params.waterCard)
  if (params.alertName) q.append('alertName', params.alertName)
  if (params.openedFrom) q.append('openedFrom', params.openedFrom)
  if (params.openedTo) q.append('openedTo', params.openedTo)
  if (params.closedFrom) q.append('closedFrom', params.closedFrom)
  if (params.closedTo) q.append('closedTo', params.closedTo)
  q.append('langKey', params.langKey ?? 'he')
  await downloadBlob(
    `${API_BASE_URL}/api/notifications/${accountId}/all/export?${q}`,
    'all-notifications.xlsx',
    { method: 'get', onProgress }
  )
}

/**
 * Export consumer notifications to Excel.
 * GET /api/notifications/{accountId}/consumer/{premiseId}/export
 */
export const exportConsumerNotificationsExcel = async (
  accountId: string | number,
  premiseId: string | number,
  params: AlertListWindow & {
    sortBy?: string
    sortOrder?: string
    includeClosed?: boolean
    langKey?: string
  },
  onProgress?: (percent: number) => void
): Promise<void> => {
  const q = new URLSearchParams(windowParams(params))
  if (params.sortBy) q.append('sortBy', params.sortBy)
  if (params.sortOrder) q.append('sortOrder', params.sortOrder)
  if (params.includeClosed !== undefined)
    q.append('includeClosed', String(params.includeClosed))
  q.append('langKey', params.langKey ?? 'he')
  await downloadBlob(
    `${API_BASE_URL}/api/notifications/${accountId}/consumer/${premiseId}/export?${q}`,
    'consumer-notifications.xlsx',
    { method: 'get', onProgress }
  )
}

// =============================================================================
// Alert types management — ניהול התראות tab
// (Backend: AlertTypesController + AlertTagsController in smartnet_api_gateway)
// =============================================================================

export type APIAlertTag = { tagId: number; tagName: string }

export type APIAlertTypeDetails = {
  alertTypeId: number
  alertTypeName: string
  entityType: string
  isEnabled: boolean
  description: string | null
  severityHint: 'LOW' | 'MILD' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH' | null
  createdBy: string | null
  createdAt: string
  updatedAt: string
  candidateCount: number
  /** When the count was last calculated. null: never. Absent from a Gateway older than the refresh. */
  candidateCountComputedAt?: string | null
  tags: APIAlertTag[]
  /** How many open alerts the request closed. Present only when it asked to close them. */
  closedAlerts?: number | null
}

export type AlertTypeCount = {
  alertTypeId: number
  candidateCount: number
  computedAt: string | null
}

export type AlertTypeManageListParams = {
  page?: number
  size?: number
  search?: string
  tagIds?: number[]
  severityHint?: string
  enabled?: boolean
}

export const listAlertTypesForManage = async (
  accountId: string | number,
  params: AlertTypeManageListParams = {}
): Promise<{
  total: number
  page: number
  pageSize: number
  result: APIAlertTypeDetails[]
}> => {
  const res = await axios.get(
    `/api/notifications/${accountId}/alert-types/manage`,
    { params }
  )
  return res.data?.data ?? { total: 0, page: 1, pageSize: 0, result: [] }
}

export const getAlertType = async (
  accountId: string | number,
  id: number
): Promise<APIAlertTypeDetails> => {
  const res = await axios.get(
    `/api/notifications/${accountId}/alert-types/${id}`
  )
  return res.data?.data
}

export const createAlertType = async (
  accountId: string | number,
  body: {
    alertTypeName: string
    entityType?: string
    description?: string
    severityHint?: string
    tagIds?: number[]
    /** The first tree. The Gateway publishes it with the type, so a refused tree leaves no type behind. */
    initialTree?: { tree: Record<string, unknown>; notes?: string | null }
  }
): Promise<APIAlertTypeDetails> => {
  const res = await axios.post(
    `/api/notifications/${accountId}/alert-types`,
    body
  )
  return res.data?.data
}

export const updateAlertTypeText = async (
  accountId: string | number,
  id: number,
  body: { alertTypeName?: string; description?: string; severityHint?: string }
): Promise<APIAlertTypeDetails> => {
  const res = await axios.patch(
    `/api/notifications/${accountId}/alert-types/${id}/text`,
    body
  )
  return res.data?.data
}

export const setAlertTypeEnabled = async (
  accountId: string | number,
  id: number,
  isEnabled: boolean,
  closeOpenAlerts = false
): Promise<APIAlertTypeDetails> => {
  const res = await axios.patch(
    `/api/notifications/${accountId}/alert-types/${id}/enabled`,
    { isEnabled, closeOpenAlerts }
  )
  return res.data?.data
}

/** How many alerts of this type are open now. */
export const getAlertTypeOpenAlerts = async (
  accountId: string | number,
  id: number
): Promise<number> => {
  const res = await axios.get(
    `/api/notifications/${accountId}/alert-types/${id}/open-alerts`
  )
  return res.data?.data?.openAlerts ?? 0
}

export const cloneAlertType = async (
  accountId: string | number,
  id: number
): Promise<APIAlertTypeDetails> => {
  const res = await axios.post(
    `/api/notifications/${accountId}/alert-types/${id}/clone`
  )
  return res.data?.data
}

/** Recounts the end units of one alert type, or of every type when `id` is omitted. Takes up to ~10 s. */
export const refreshAlertTypeCounts = async (
  accountId: string | number,
  id?: number
): Promise<AlertTypeCount[]> => {
  const path =
    id === undefined
      ? `/api/notifications/${accountId}/alert-types/refresh-counts`
      : `/api/notifications/${accountId}/alert-types/${id}/refresh-counts`
  const res = await axios.post(path, undefined, { timeout: 100_000 })
  return res.data?.data ?? []
}

export const deleteAlertType = async (
  accountId: string | number,
  id: number
): Promise<boolean> => {
  const res = await axios.delete(
    `/api/notifications/${accountId}/alert-types/${id}`
  )
  return res.data?.success === true
}

export const attachAlertTypeTags = async (
  accountId: string | number,
  id: number,
  tagIds: number[]
): Promise<APIAlertTypeDetails> => {
  const res = await axios.post(
    `/api/notifications/${accountId}/alert-types/${id}/tags`,
    { tagIds }
  )
  return res.data?.data
}

export const detachAlertTypeTag = async (
  accountId: string | number,
  id: number,
  tagId: number
): Promise<APIAlertTypeDetails> => {
  const res = await axios.delete(
    `/api/notifications/${accountId}/alert-types/${id}/tags/${tagId}`
  )
  return res.data?.data
}

// Tags

export const listAlertTags = async (
  accountId: string | number
): Promise<APIAlertTag[]> => {
  const res = await axios.get(`/api/notifications/${accountId}/alert-tags`)
  return res.data?.data ?? []
}

export const createAlertTag = async (
  accountId: string | number,
  tagName: string
): Promise<APIAlertTag> => {
  const res = await axios.post(`/api/notifications/${accountId}/alert-tags`, {
    tagName,
  })
  return res.data?.data
}

export const deleteAlertTag = async (
  accountId: string | number,
  tagId: number
): Promise<boolean> => {
  const res = await axios.delete(
    `/api/notifications/${accountId}/alert-tags/${tagId}`
  )
  return res.data?.success === true
}

// =============================================================================
// Alert tree editor — דיאגרמת התראה (Spec #2)
// =============================================================================

export type APIAlertTreeVersion = {
  alertTypeId: number
  versionNo: number
  isActive: boolean
  notes: string | null
  tree: Record<string, any>
  publishedAt: string
}

export type APINotificationChannel = {
  notificationChannelId: number
  /** Absent on a Gateway that does not publish it. A tree may name a channel by this key. */
  notificationChannelKey?: string
  name: string
  category: string
  targetType: string
  /** False for a channel that is switched off. Absent on a Gateway that does not publish it, which means on. */
  isEnabled?: boolean
}

export type APIAttributeValue = {
  value: string
  label: string
}

/**
 * 'consumers': the Gateway sends no allowedValues. The values are water service
 * ids that the author finds with the consumer search.
 */
export type AttributeValueEntry = 'select' | 'consumers'

export type APIAttribute = {
  name: string
  label: string
  dataType: string
  valueEntry: AttributeValueEntry
  allowedValues: APIAttributeValue[] | null
}

/** Device-alert vocabulary entry (name nullable → show code as fallback label). */
export type APIDeviceAlertCode = {
  code: string
  name: string | null
  /** False when the code must not be offered for new conditions. Absent on an older Gateway: offered. */
  available?: boolean
}

export type APIReferenceBundle = {
  notificationChannels: APINotificationChannel[]
  entityTypes: string[]
  attributes: APIAttribute[]
  groups: APIAttribute[]
  deviceAlertCodes: APIDeviceAlertCode[]
  crmTaskTypes: string[]
}

export const EMPTY_REFERENCE_BUNDLE: APIReferenceBundle = {
  notificationChannels: [],
  entityTypes: [],
  attributes: [],
  groups: [],
  deviceAlertCodes: [],
  crmTaskTypes: [],
}

export const getActiveTree = async (
  accountId: string | number,
  alertTypeId: number
): Promise<APIAlertTreeVersion> => {
  const res = await axios.get(
    `/api/notifications/${accountId}/alert-types/${alertTypeId}/tree`
  )
  return res.data?.data
}

export const listTreeVersions = async (
  accountId: string | number,
  alertTypeId: number
): Promise<APIAlertTreeVersion[]> => {
  const res = await axios.get(
    `/api/notifications/${accountId}/alert-types/${alertTypeId}/tree/versions`
  )
  return res.data?.data ?? []
}

export const saveTree = async (
  accountId: string | number,
  alertTypeId: number,
  tree: Record<string, any>,
  notes?: string
): Promise<APIAlertTreeVersion> => {
  const res = await axios.put(
    `/api/notifications/${accountId}/alert-types/${alertTypeId}/tree`,
    { tree, notes: notes ?? null }
  )
  return res.data?.data
}

// Older Gateways omit valueEntry; every attribute they send is a select.
const parseAttribute = (
  wire: Omit<APIAttribute, 'valueEntry'> & { valueEntry?: unknown }
): APIAttribute => ({
  ...wire,
  valueEntry: wire.valueEntry === 'consumers' ? 'consumers' : 'select',
})

export const getReferenceBundle = async (
  accountId: string | number,
  entityType: string = 'premise'
): Promise<APIReferenceBundle> => {
  const res = await axios.get(`/api/notifications/${accountId}/reference`, {
    params: { entityType, langKey: 'he' },
  })
  const bundle = res.data?.data
  if (!bundle) return EMPTY_REFERENCE_BUNDLE
  return {
    ...bundle,
    attributes: (bundle.attributes ?? []).map(parseAttribute),
    groups: (bundle.groups ?? []).map(parseAttribute),
  }
}

/** A water service as the consumer picker shows it. `serviceId` is what a saved tree stores. */
export type APIConsumerRef = {
  serviceId: number
  waterCard: number | null
  name: string | null
  address: string | null
}

/** One text, matched by the Gateway across the same fields as the consumers page search. */
export const searchAlertConsumers = async (
  accountId: string | number,
  q: string,
  signal?: AbortSignal,
  limit?: number
): Promise<{ items: APIConsumerRef[]; hasMore: boolean }> => {
  const res = await axios.get(
    `/api/notifications/${accountId}/reference/consumers`,
    { params: { q, limit }, signal }
  )
  return res.data?.data ?? { items: [], hasMore: false }
}

export const resolveAlertConsumers = async (
  accountId: string | number,
  serviceIds: number[]
): Promise<APIConsumerRef[]> => {
  const res = await axios.post(
    `/api/notifications/${accountId}/reference/consumers/resolve`,
    { serviceIds }
  )
  return res.data?.data ?? []
}

// =============================================================================
// Send/resend/palette/channels — SMS notification composition
// =============================================================================

export type SendAudience =
  | { kind: 'premises'; ids: number[] }
  | { kind: 'group'; groupId: number }
  | {
      kind: 'area'
      vertices: { longitude: number; latitude: number }[]
      // The map table's column filters and search, polygon excluded. The Gateway
      // lists the premises with them, so a send reaches the consumers the table shows.
      filters: ApiResourceFilter[]
    }
  // The consumers list's own search and column filters. The Gateway resolves
  // them with the same consumers search, so the send reaches every page.
  | { kind: 'search'; filters: ApiResourceFilter[] }

export type SendCounts = {
  premisesResolved: number
  premisesWithoutTargets: number
  // not rendered in v1 (deferred disclaimer) — TODO: revisit
  // area sends: premises of this site that match the table's filters but have no coordinates
  premisesWithoutCoordinates: number
  notificationsEnqueued: number
  // tree and group sends: members of another site that the Gateway left out
  premisesOutsideSite?: number
}

export type TemplateVariable = { variable: string; label: string }
export type SendChannel = { channelId: number; category: string; name: string }

/** One channel's own text, keyed by channel id as a string on the wire. */
export type SendChannelContent = Record<
  string,
  { body: string; subject?: string }
>

export type SendRequest = {
  audience: SendAudience
  channelIds: number[]
  body: string
  channelContent?: SendChannelContent
  reason?: string
  dryRun: boolean
  idempotencyKey?: string
  segmentsEstimate?: { encoding: string; chars: number; segments: number }
}

export const buildSendBody = (r: SendRequest): Record<string, unknown> => {
  const out: Record<string, unknown> = {
    channelIds: r.channelIds,
    body: r.body,
    dryRun: r.dryRun,
  }
  if (r.reason) out.reason = r.reason
  if (r.channelContent) out.channelContent = r.channelContent
  if (!r.dryRun && r.idempotencyKey) out.idempotencyKey = r.idempotencyKey
  if (r.segmentsEstimate) out.segmentsEstimate = r.segmentsEstimate
  switch (r.audience.kind) {
    case 'premises':
      out.premiseIds = r.audience.ids
      break
    // The Gateway sends to the consumers that the zone's consumers tab lists.
    case 'group':
      out.groupId = r.audience.groupId
      break
    case 'area':
      out.polygon = r.audience.vertices
      out.apiFilter = {
        apiResources: r.audience.filters,
        logicalOperator: 'AND',
      }
      break
    case 'search':
      out.apiFilter = {
        apiResources: r.audience.filters,
        logicalOperator: 'AND',
      }
      break
  }
  return out
}

export const sendNotifications = async (
  accountId: number,
  request: SendRequest
): Promise<SendCounts> => {
  const res = await axios.post(
    `/api/notifications/${accountId}/send`,
    buildSendBody(request)
  )
  return res.data?.data
}

export const resendAlertNotifications = async (
  accountId: number,
  alertId: number
): Promise<SendCounts> => {
  const res = await axios.post(
    `/api/notifications/${accountId}/alert/${alertId}/resend`
  )
  return res.data?.data
}

export const getTemplateVariables = async (
  accountId: number
): Promise<TemplateVariable[]> => {
  const res = await axios.get(
    `/api/notifications/${accountId}/template-variables`
  )
  return res.data?.data ?? []
}

export const getNotificationChannels = async (
  accountId: number
): Promise<SendChannel[]> => {
  const res = await axios.get(`/api/notifications/${accountId}/channels`)
  return res.data?.data ?? []
}

export type ConsumerTargets = { phones: string[]; emails: string[] }

/**
 * The addresses a send to this premise reaches (enabled notification targets).
 * A value shows once; the Gateway already lists it once and this keeps the
 * dropdowns right if a response ever repeats one.
 */
export const getConsumerTargets = async (
  accountId: number,
  premiseId: number
): Promise<ConsumerTargets> => {
  const res = await axios.get(
    `/api/notifications/${accountId}/consumer/${premiseId}/targets`
  )
  const distinct = (values: unknown): string[] => [
    ...new Set(
      (Array.isArray(values) ? values : [])
        .map(v => String(v).trim())
        .filter(Boolean)
    ),
  ]
  return {
    phones: distinct(res.data?.data?.phones),
    emails: distinct(res.data?.data?.emails),
  }
}

export const getSenderName = async (
  accountId: number
): Promise<string | null> => {
  const res = await axios.get(`/api/notifications/${accountId}/sender-name`)
  return res.data?.data?.fromName ?? null
}

export const verifySenderName = async (
  accountId: number,
  fromName: string
): Promise<{ approved: boolean; providerResponse: string }> => {
  const res = await axios.post(`/api/notifications/${accountId}/sender-name`, {
    fromName,
  })
  return res.data?.data
}
